import { api } from './api';

export interface DocumentWorkflowStepApproverDto {
  id: string;
  documentWorkflowStepId: string;
  approverId: string;
  status: string;
  reviewedAt?: string | null;
  comment?: string;
}

export interface DocumentWorkflowStepDto {
  id: string;
  documentWorkflowId: string;
  workflowStepId: string;
  stepOrder: number;
  approvalRule: string;
  status: string;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  comment?: string | null;
  dueDate?: string | null;
  startedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  approvers: DocumentWorkflowStepApproverDto[];
  /** Snapshot do WorkflowStep; permite botões (SendForReview etc.) se o GET do modelo falhar. */
  definitionAllowedActions?: string[] | null;
  definitionRequiresComment?: boolean;
  definitionOrder?: number;
  definitionType?: string | null;
  definitionName?: string | null;
  definitionDescription?: string | null;
}

export interface DocumentWorkflowDto {
  id: string;
  documentId: string;
  workflowId: string;
  documentVersionId?: string | null;
  status: string;
  source: string;
  startedAt: string;
  completedAt?: string | null;
  startedBy?: string | null;
  steps: DocumentWorkflowStepDto[];
  disabledActions?: DisabledWorkflowActionDto[];
}

export interface DisabledWorkflowActionDto {
  action: string;
  reason: string;
}

export interface WorkflowInboxItemDto {
  documentId: string;
  documentTitle: string;
  documentWorkflowId: string;
  documentWorkflowStepId: string;
  stepName: string;
  stepType: string;
  approverStatus: string;
  dueDate?: string | null;
  updatedAt: string;
}

export interface DocumentWorkflowAuditDto {
  id: string;
  documentWorkflowId: string;
  documentId: string;
  documentVersionId?: string | null;
  userId?: string | null;
  userName: string;
  action: string;
  fromStatus: string;
  toStatus: string;
  comment?: string | null;
  details?: string | null;
  createdAt: string;
}

export interface PaginatedResult<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ExecuteWorkflowTransitionResult {
  success: boolean;
  newStatus: string;
  errorMessage?: string | null;
  newDocumentId?: string | null;
}

function unwrapAxiosMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  const rawMessage = e?.response?.data?.message || e?.message || 'Erro na requisição';
  return sanitizeApiMessage(rawMessage);
}

function sanitizeApiMessage(message: string): string {
  const normalized = message.toLowerCase();
  const technicalMarkers = [
    'linq expression',
    'could not be translated',
    'dbset<',
    'entityframework',
    'stack trace',
    ' at ',
  ];

  const isTechnical = technicalMarkers.some(marker => normalized.includes(marker));
  if (isTechnical) {
    return 'Não foi possível concluir a ação no momento. Tente novamente e, se persistir, contate o suporte.';
  }

  return message;
}

class DocumentWorkflowService {
  async getMyWorkflowInbox(): Promise<WorkflowInboxItemDto[]> {
    try {
      const { data } = await api.get<WorkflowInboxItemDto[]>('/document-workflow/inbox');
      return data ?? [];
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async initiateWorkflow(documentId: string, workflowId: string): Promise<DocumentWorkflowDto> {
    try {
      const { data } = await api.post<DocumentWorkflowDto>(
        `/document-workflow/${documentId}/initiate`,
        { workflowId }
      );
      return data;
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async executeTransition(
    documentId: string,
    action: string,
    comment?: string
  ): Promise<ExecuteWorkflowTransitionResult> {
    try {
      const { data } = await api.post<ExecuteWorkflowTransitionResult>(
        `/document-workflow/${documentId}/transition`,
        { action, comment: comment ?? null }
      );
      return data;
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async approve(documentId: string, comment?: string): Promise<ExecuteWorkflowTransitionResult> {
    try {
      const { data } = await api.post<ExecuteWorkflowTransitionResult>(
        `/document-workflow/${documentId}/approve`,
        { comment: comment ?? null }
      );
      return data;
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getDocumentWorkflow(documentId: string): Promise<DocumentWorkflowDto | null> {
    try {
      const { data } = await api.get<DocumentWorkflowDto>(`/document-workflow/${documentId}`);
      return data;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async assignWorkflow(documentId: string, workflowId: string): Promise<DocumentWorkflowDto> {
    try {
      const { data } = await api.put<DocumentWorkflowDto>(`/document-workflow/${documentId}/assign`, {
        workflowId,
      });
      return data;
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async removeWorkflow(documentId: string): Promise<void> {
    try {
      await api.delete(`/document-workflow/${documentId}`);
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async getAuditHistory(documentId: string): Promise<DocumentWorkflowAuditDto[]> {
    const pageSize = 200;
    try {
      const all: DocumentWorkflowAuditDto[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const { data } = await api.get<PaginatedResult<DocumentWorkflowAuditDto>>(
          `/document-workflow/${documentId}/audit`,
          { params: { page, pageSize } }
        );
        const chunk = data?.data ?? [];
        all.push(...chunk);
        totalPages = data?.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages);

      return all.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async exportAuditCsv(documentId: string): Promise<Blob> {
    const response = await api.get<Blob>(`/document-workflow/${documentId}/audit/export`, {
      params: { format: 'csv' },
      responseType: 'blob',
      validateStatus: () => true,
    });
    if (response.status >= 400) {
      let message = 'Erro ao exportar auditoria';
      try {
        const text = await new Response(response.data as Blob).text();
        try {
          const j = JSON.parse(text) as { message?: string };
          if (j.message) message = j.message;
        } catch {
          if (text) message = text;
        }
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    return response.data;
  }
}

export const documentWorkflowService = new DocumentWorkflowService();
